import { IAlertProps } from "@components/alert/Alert.types";
import { WithBusyIndicator, withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { TDisabledDateUnit } from "@components/inputs/date/popup/Calendar.utils";
import { FIELD_VER_MARGIN, FIELD_VER_MARGIN_SMALL } from "@components/inputs/field/Field.styles";
import { SimpleTimelineItem } from "@components/simpleTimeline/SimpleTimeline";
import {
    SIMPLE_TIMELINE_POINT_MARGIN,
    SIMPLE_TIMELINE_POINT_SIZE,
    SimpleTimeLineStatus
} from "@components/simpleTimeline/SimpleTimeline.utils";
import { getInfoValue, ifAny, IFieldDef, IGetValueArgs, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { getBoundValue } from "@odata/Data.utils";
import { IFieldInfo } from "@odata/FieldInfo.utils";
import { TemporalEntityEntity } from "@odata/GeneratedEntityTypes";
import { PrEntityValueSourceCode } from "@odata/GeneratedEnums";
import {
    createNewTemporalPropertyBagItem,
    EntityValueSourcePropNameEnumSuffix,
    getCurrentBagFromUngrouped,
    getDecisiveDateForHistoryValues,
    getDefaultTemporalDateValidFrom,
    getDefaultTemporalDateValidTo,
    getEndOfGranularity,
    getGranularity,
    getGroupingProperties,
    getTimelineStatus,
    groupAndOptimizeRanges,
    ISmartTemporalPropertyDialogCustomData,
    markHistoryValuesNotSynced,
    markSynchronizedRanges,
    SourceCodeLocalPath,
    TemporalEntityProp,
    TTemporal,
    ungroupAndOptimizeRanges
} from "@odata/TemporalUtils";
import { isDefined } from "@utils/general";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import { cloneDeep } from "lodash";
import React from "react";

import SmartFastEntryList, {
    ActionType,
    ICustomPreContentArgs,
    ISmartFastEntriesActionEvent
} from "../../components/smart/smartFastEntryList";
import { FieldType, LabelStatus, Status, ValidationErrorType } from "../../enums";
import { IValidationError } from "../../model/Validator.types";
import BindingContext, { createBindingContext, IEntity } from "../../odata/BindingContext";
import DateType, { DATE_MAX, DATE_MIN, getUtcDayjs } from "../../types/Date";
import { isFormReadOnly } from "../../views/formView/Form.utils";
import { FormStorage } from "../../views/formView/FormStorage";
import ConfirmationButtons from "../../views/table/ConfirmationButtons";
import { DialogStyledTemporalPropertyDialog } from "./SmartTemporalPropertyDialog.styles";

interface IProps extends WithBusyIndicator {
    storage: FormStorage;
    bindingContext: BindingContext;
    temporalItemBindingContext: BindingContext;
    onChange?: (args: ISmartFieldChange) => void;
    onBlur?: (args: ISmartFieldBlur) => void;
    onClose?: (bindingContext: BindingContext, current?: TTemporal) => void;
}

const startOfQuarterDisabilityCheck = (date: Date, unit: TDisabledDateUnit) => {
    if (unit !== "month") {
        return false;
    }
    // only allow first month of each quarter (0, 3, 6, 9)
    return !!(date.getMonth() % 3);
};

class SmartTemporalPropertyDialog extends React.Component<IProps> {
    oldTemporalBag: TTemporal[] = null;
    oldOrigTemporalBag: TTemporal[] = null;
    // DateValidFrom/To fields has shared definitions for whole property bag
    // SmartTemporalPropertyDialog changes the definition dynamically with correct configuration,
    // backups are used to restore original values when the dialog is closed
    dateValidFromFieldInfoBackup: IFieldInfo;
    dateValidToFieldInfoBackup: IFieldInfo;

    _isSynced = false;

    constructor(props: IProps) {
        super(props);

        const { storage } = this;
        const { bindingContext } = this.props;
        const dialogSettings = this.getDialogSettings();

        storage.setCustomData({
            openedDialogBindingContext: bindingContext,
            dialogSettings,
            dateBeforeDecisiveDateMsg: dialogSettings.dateBeforeDecisiveDateMsg
        });
    }

    componentDidMount() {
        this.init()
            .then(() => this.props.setBusy(false));
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<{}>, snapshot?: any) {
        if (!this.props.bindingContext.getRootParent().isSame(this.props.storage.data.bindingContext)) {
            // close dialog on url/row change
            this.close();
        }
    }

    componentWillUnmount() {
        const dateValidFromPath = this.props.temporalItemBindingContext.navigate(`${this.temporalPropertyBagPath}/DateValidFrom`).toString(true);
        const dateValidToPath = this.props.temporalItemBindingContext.navigate(`${this.temporalPropertyBagPath}/DateValidTo`).toString(true);

        this.props.storage.data.fieldsInfo[dateValidFromPath] = this.dateValidFromFieldInfoBackup;
        this.props.storage.data.fieldsInfo[dateValidToPath] = this.dateValidToFieldInfoBackup;

        this.clearItemsInfo(this.props);

        (this.props.storage as FormStorage<unknown, ISmartTemporalPropertyDialogCustomData>).setCustomData({
            openedDialogBindingContext: null,
            dialogSettings: null,
            dateBeforeDecisiveDateMsg: null
        });
    }

    get storage() {
        return this.props.storage as FormStorage<unknown, ISmartTemporalPropertyDialogCustomData>;
    }

    async init(): Promise<void> {

        const { storage } = this;
        const { bindingContext } = this.props;
        const dialogSettings = this.getDialogSettings();

        const groupingProperties = this.getGroupingProperties();
        this.oldTemporalBag = this.entity[this.temporalPropertyBagPath];
        this.oldOrigTemporalBag = this.origEntity[this.temporalPropertyBagPath];
        let newBag: TTemporal[] = this.entity[this.temporalPropertyBagPath];

        if (dialogSettings.extractHistory) {
            // we work with history values, mark all empty ranges with "SourceCode", so they are not cleared using "groupAndOptimizeRanges"
            newBag = markSynchronizedRanges(newBag, groupingProperties);
        }

        newBag = groupAndOptimizeRanges(newBag, groupingProperties);

        this._isSynced = !!newBag.find(item => isDefined(item[SourceCodeLocalPath]));

        if (dialogSettings.extractHistory) {
            newBag = await dialogSettings.extractHistory(storage, this.entity, newBag);
        }

        if (!newBag?.length) {
            const emptyItem = createNewTemporalPropertyBagItem({}, { tempSettings: dialogSettings, storage, bindingContext });
            const withoutGaps = getInfoValue(dialogSettings, "withoutGaps", { storage, bindingContext });
            if (withoutGaps) {
                // todo: if there is already some temporal bag but with other properties, function createNewTemporalPropertyBagItem, chooses dateStart as today look into it, shouldn't it always start with DATE_MIN?
                emptyItem.DateValidFrom = DATE_MIN;
            }
            newBag = [emptyItem];

            // remove #id, item will be given Id in the next few lines
            delete emptyItem[BindingContext.NEW_ENTITY_ID_PROP];
        }

        this.entity[this.temporalPropertyBagPath] = newBag.map((item, index) => ({
            ...item,
            // not saved items in the property bag can be without Id,
            // so we are not extracting it in groupTemporalRanges at all,
            // and instead we can just use indexes, this Id is not used anyway, it is just needed for SmartFastEntry list to work properly
            Id: index
        }));

        if (dialogSettings.extractHistory) {
            // we work with history values, mark all empty ranges with "SourceCode", so they are not cleared using "groupAndOptimizeRanges"
            this.oldOrigTemporalBag = markSynchronizedRanges(this.oldOrigTemporalBag, groupingProperties);
        }


        // we need to convert even origEntity.TemporalPropertyBag,
        // so that we can use it in callbacks, e.g. to find SimpleTimeLineStatus.History items
        this.origEntity[this.temporalPropertyBagPath] = groupAndOptimizeRanges(this.oldOrigTemporalBag, groupingProperties).map((item, index) => ({
            ...item,
            Id: index
        }));

        const bcWithoutKeys = createBindingContext(this.props.temporalItemBindingContext.getFullPath(true), this.props.storage.oData.metadata);

        // TODO: it's not good way to change field info, as it is copied to each item in collection, so for now we set type to field info without keys
        // but it would be better to have just one field info per path, and save select items somewhere else, or have some other way to pass field type from granularity directly to smartField, without changing field info
        const dateValidFromFieldInfoWithoutKeys = this.props.storage.getInfo(bcWithoutKeys.navigate(`${this.temporalPropertyBagPath}/DateValidFrom`));
        const dateValidToFieldInfoWithoutKeys = this.props.storage.getInfo(bcWithoutKeys.navigate(`${this.temporalPropertyBagPath}/DateValidTo`));

        const dateValidFromFieldInfo = this.props.storage.getInfo(this.props.temporalItemBindingContext.navigate(`${this.temporalPropertyBagPath}/DateValidFrom`));
        const dateValidToFieldInfo = this.props.storage.getInfo(this.props.temporalItemBindingContext.navigate(`${this.temporalPropertyBagPath}/DateValidTo`));

        // backup field infos,
        // alter them just for the current use in the dialog
        // restore them when the dialog is closed
        this.dateValidFromFieldInfoBackup = cloneDeep(dateValidFromFieldInfo);
        this.dateValidToFieldInfoBackup = cloneDeep(dateValidToFieldInfo);

        const granularity = getGranularity(dialogSettings, {
            storage: this.props.storage,
            bindingContext: this.props.temporalItemBindingContext
        });

        if (["month", "quarter"].includes(granularity)) {
            dateValidFromFieldInfoWithoutKeys.type = FieldType.MonthYear;
            dateValidToFieldInfoWithoutKeys.type = FieldType.MonthYear;
        }

        const dateValidToIsDisabled = (args: IGetValueArgs) => {
            const items = this.getItems();
            const currentItemBc = args.bindingContext.getParent();
            const currentItemIndex = items.findIndex(item => currentItemBc.isSame(this.getBindingContext().addKey(item)));
            const isLast = items.length - 1 === currentItemIndex;

            if (dialogSettings?.cannotBeEnded) {
                if (isLast) {
                    return true;
                }
            }

            const withoutGaps = getInfoValue(dialogSettings, "withoutGaps", {
                storage: args.storage,
                bindingContext: currentItemBc
            });

            if (withoutGaps) {
                if (!isLast) {
                    return true;
                }
            }

            return false;
        };

        if (dateValidToFieldInfo.isDisabled) {
            dateValidToFieldInfo.isDisabled = ifAny(dateValidToFieldInfo.isDisabled, dateValidToIsDisabled);
        } else {
            dateValidToFieldInfo.isDisabled = dateValidToIsDisabled;
        }

        if (dialogSettings?.dateType) {
            dateValidFromFieldInfoWithoutKeys.type = dialogSettings.dateType;
            dateValidToFieldInfoWithoutKeys.type = dialogSettings.dateType;
        }

        if (dialogSettings?.isCalendarDateDisabledDateValidFrom) {
            if (!dateValidFromFieldInfo.fieldSettings) {
                dateValidFromFieldInfo.fieldSettings = {};
            }

            dateValidFromFieldInfo.fieldSettings.isDateDisabled = dialogSettings.isCalendarDateDisabledDateValidFrom;
        } else if (granularity === "quarter") {
            // disable other months than first month of quarter
            dateValidFromFieldInfo.fieldSettings.isDateDisabled = startOfQuarterDisabilityCheck;
        }

        if (dialogSettings?.isCalendarDateDisabledDateValidTo) {
            if (!dateValidToFieldInfo.fieldSettings) {
                dateValidToFieldInfo.fieldSettings = {};
            }

            dateValidToFieldInfo.fieldSettings.isDateDisabled = dialogSettings.isCalendarDateDisabledDateValidTo;
        } else if (granularity === "quarter") {
            // disable other months than first month of quarter
            dateValidToFieldInfo.fieldSettings.isDateDisabled = startOfQuarterDisabilityCheck;
        }

        this.clearItemsInfo(this.props);
    }

    /** Usually, temporal properties are inside TemporalPropertyBag collection.
     * We have one case in Company, with collection that contains DateValidFrom/DateValidTo,
     * but has different name, because it was created before backend implemented temporal property concept.
     * Since BE guys doesn't seem to be keen on changing that, use custom path or get the collection from binding context instead of using hardcoded TemporalPropertyBag.*/
    getTemporalPropertyBagPath = memoizeOne((): string => {
        return this.props.bindingContext.getParent().getPath(true);
    }, () => [this.props.bindingContext.toString()]);

    get temporalPropertyBagPath(): string {
        return this.getTemporalPropertyBagPath();
    }

    getEntity = memoizeOne((): IEntity => {
        return getBoundValue({
            data: this.props.storage.data.entity,
            bindingContext: this.props.temporalItemBindingContext,
            dataBindingContext: this.props.storage.data.bindingContext
        });
    }, () => [this.props.storage.data.entity, this.props.temporalItemBindingContext]);

    getOrigEntity = memoizeOne((): IEntity => {
        return getBoundValue({
            data: this.props.storage.data.origEntity,
            bindingContext: this.props.temporalItemBindingContext,
            dataBindingContext: this.props.storage.data.bindingContext
        }) ?? {};
    }, () => [this.props.storage.data.origEntity, this.props.temporalItemBindingContext]);

    get entity(): IEntity {
        return this.getEntity();
    }

    get origEntity(): IEntity {
        return this.getOrigEntity();
    }

    validateDateGranularity(bc: BindingContext): boolean {
        const dialogSettings = this.getDialogSettings();
        const granularity = getGranularity(dialogSettings, {
            storage: this.props.storage,
            bindingContext: this.props.temporalItemBindingContext
        });
        const value = this.props.storage.getValue(bc) as Date;
        const current = value && getUtcDayjs(value as Date);
        const isDateTo = bc.getPath() === TemporalEntityEntity.DateValidTo;
        let isValid = true;

        if (!current) {
            return false;
        }

        if (current.isSame(DATE_MAX) || current.isSame(DATE_MIN)) {
            isValid = true;
        } else if (typeof dialogSettings.getCustomDateGranularity === "function") {
            const prevDay = current.subtract(1, "day").startOf("day");
            let nextGranularityDay = dialogSettings.getCustomDateGranularity(prevDay);
            if (isDateTo) {
                // for DateValidTo we need to check the day before the next granularity day
                // as it should be the day before the next DateValidFrom granularity date
                nextGranularityDay = nextGranularityDay.subtract(1, "day");
            }
            isValid = current.isSame(nextGranularityDay, granularity);
        } else if (granularity !== "day") {
            isValid = current.isValid() &&
                (isDateTo ? current.endOf(granularity).isSame(current, "day") : current.startOf(granularity).isSame(current, "day"));
        }

        if (!isValid) {
            this.props.storage.setError(bc, {
                message: this.props.storage.t("Components:TemporalProperty.ValidationInvalidDateForGranularity"),
                errorType: ValidationErrorType.Field
            });
            return false;
        }
        return isValid;
    }

    clearItemsInfo = (props: IProps): void => {
        // clear field info for items, so that the changes done to it here, won't be transferred to another dialog
        // => the items (their SmartField) will be forced to copy the field info from the default one in each dialog
        props.storage.clearCollectionInfoByPath(`${this.props.temporalItemBindingContext.toString()}/${this.temporalPropertyBagPath}(`);
    };

    isItemRemovable = (args: IGetValueArgs): boolean => {
        // todo not sure how this should actually work
        // => keep line commented for now
        // return this.getItems().length > 1 && isTemporalLineItemRemovable(args);

        // this should support our only case (VatRegistered state in company form)
        const items = this.getItems();
        const itemIndex = items.findIndex(item => args.bindingContext.isSame(args.bindingContext.removeKey().addKey(item)));

        return itemIndex !== 0 && itemIndex === items.length - 1;
    };

    getGroupingProperties = memoizeOne((): string[] => {
        return getGroupingProperties(this.props.bindingContext, this.props.storage);
    });

    getColumns = memoizeOne((): IFieldDef[] => {
        const dialogSettings = this.getDialogSettings();

        return [
            ...(dialogSettings.columns ? dialogSettings.columns.map(col => ({ id: col })) : [{ id: this.props.bindingContext.getPath() }]),
            { id: "DateValidFrom" },
            { id: "DateValidTo" }
        ];
    }, () => [this.props.bindingContext]);

    close = (current?: TTemporal<Record<string, unknown>>): void => {
        this.clearAllErrors();

        this.props.onClose?.(this.props.bindingContext, current);
        this.props.storage.setCustomData({ temporalPropertyDialogBc: null });
        this.props.storage.refresh();
    };

    getBindingContext = memoizeOne(() => {
        return this.props.temporalItemBindingContext.navigate(this.temporalPropertyBagPath);
    });

    updateDateValidToBasedOnNextValidFrom = (dateValidToBc: BindingContext, nextDateValidFrom: Date): void => {
        const newPreviousItemDateValidTo = getUtcDayjs(nextDateValidFrom).subtract(1, "day").toDate();

        if (DateType.isValid(newPreviousItemDateValidTo)) {
            this.props.storage.clearError(dateValidToBc);
            this.props.storage.setValue(dateValidToBc, newPreviousItemDateValidTo);
        }
    };

    setItemDefaultValues = (item: TTemporal): void => {
        const items = this.getItems();
        const newItemBc = this.getBindingContext().addKey(item);
        const newItemIndex = items.length - 1;
        const previousItem = items[newItemIndex - 1];
        const dialogSettings = this.getDialogSettings();
        const previousItems = items.slice(0, -1);
        const decisiveDate = getDecisiveDateForHistoryValues(this.props.storage, { dialogSettings });

        for (const column of this.getColumns()) {
            const colBc = newItemBc.navigate(column.id);

            if (column.id === "DateValidFrom") {
                const withoutGaps = getInfoValue(dialogSettings, "withoutGaps", {
                    storage: this.props.storage,
                    bindingContext: newItemBc
                });
                this.props.storage.setValue(colBc, getDefaultTemporalDateValidFrom(dialogSettings, previousItems, {
                    storage: this.props.storage,
                    bindingContext: newItemBc.getParent()
                }, decisiveDate, withoutGaps, ));
            } else if (column.id === "DateValidTo") {
                this.props.storage.setValue(colBc, getDefaultTemporalDateValidTo(dialogSettings, previousItems));
            } else {
                this.props.storage.setValue(colBc, cloneDeep(previousItem[column.id]));
            }
        }

        const updatedNewItem = this.props.storage.getValue(newItemBc) as TTemporal;

        this.updateDateValidToBasedOnNextValidFrom(this.getBindingContext().addKey(previousItem).navigate("DateValidTo"), updatedNewItem.DateValidFrom);
    };

    validateDates = (): boolean => {
        const itemsBc = this.getBindingContext();
        const items = this.getItems();
        let anyError = false;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const itemBc = itemsBc.addKey(item);
            const dateValidFromBc = itemBc.navigate("DateValidFrom");
            const dateValidToBc = itemBc.navigate("DateValidTo");
            let dateFromHasError = false;
            let dateToHasError = false;

            // compare dates in the current item
            if (item.DateValidFrom && item.DateValidTo && getUtcDayjs(item.DateValidFrom).isSameOrAfter(item.DateValidTo, "date")) {
                dateFromHasError = true;
                dateToHasError = true;
                const error: IValidationError = {
                    message: this.props.storage.t("Components:TemporalProperty.ValidationDateFromBeforeDateTo"),
                    errorType: ValidationErrorType.Field
                };

                this.props.storage.setError(dateValidFromBc, error);
                this.props.storage.setError(dateValidToBc, error);
            }

            // compare with previous items
            if (i > 0) {
                const previousItem = items[i - 1];

                if (previousItem.DateValidTo && item.DateValidFrom && getUtcDayjs(item.DateValidFrom).isSameOrBefore(previousItem.DateValidTo, "date")) {
                    dateFromHasError = true;

                    this.props.storage.setError(dateValidFromBc, {
                        message: this.props.storage.t("Components:TemporalProperty.ValidationDateFromAfterPreviousDateTo"),
                        errorType: ValidationErrorType.Field
                    });
                }
            }

            // Check granularity
            dateFromHasError = dateFromHasError || !this.validateDateGranularity(dateValidFromBc);
            dateToHasError = dateToHasError || !this.validateDateGranularity(dateValidToBc);

            if (!dateFromHasError) {
                this.props.storage.clearError(dateValidFromBc);
            } else {
                anyError = true;
            }

            if (!dateToHasError) {
                this.props.storage.clearError(dateValidToBc);
            } else {
                anyError = true;
            }
        }

        return !anyError;
    };

    clearAllErrors = (): void => {
        this.props.storage.clearCollectionAdditionalFieldDataByPath(this.getBindingContext().getFullPath());
    };

    handleDateValidChange = (args: ISmartFieldChange): void => {
        const path = args.bindingContext.getPath();

        if (!["DateValidFrom", "DateValidTo"].includes(path)) {
            return;
        }
        const { storage } = this.props;

        const dialogSettings = this.getDialogSettings();

        const items = this.getItems();
        const itemBc = args.bindingContext.getParent();
        const itemIndex = items.findIndex(item => itemBc.isSame(itemBc.removeKey().addKey(item)));

        const withoutGaps = getInfoValue(dialogSettings, "withoutGaps", {
            storage,
            bindingContext: args.bindingContext
        });

        if (withoutGaps && path === "DateValidFrom") {
            const prevItem = items[itemIndex - 1];
            if (prevItem) {
                this.updateDateValidToBasedOnNextValidFrom(itemBc.removeKey().addKey(prevItem).navigate("DateValidTo"), args.value as Date);
            }
        }

        if (path === "DateValidTo") {
            // revalidate next DateValidFrom
            const nextItem = items[itemIndex + 1];

            if (nextItem) {
                const nextItemBc = itemBc.removeKey().addKey(nextItem);
                const nextDateValidFromBc = nextItemBc.navigate(TemporalEntityEntity.DateValidFrom);

                storage.addActiveField(nextDateValidFromBc, { revalidate: true });
            }
            // use the last day of period by granularity
            const getArgs: IGetValueArgs = {
                storage,
                bindingContext: this.props.temporalItemBindingContext
            };
            const granularity = getGranularity(dialogSettings, getArgs);
            if (granularity && DateType.isValid(args.value as Date) && granularity !== "day") {
                // set the value to be last day of the granularity period
                storage.setValue(args.bindingContext, getEndOfGranularity(dialogSettings, args.value as Date, getArgs).toDate());
            }
        } else {
            // revalidate previous DateValidTo
            const prevItem = items[itemIndex + 1];
            if (prevItem) {
                const prevItemBc = itemBc.removeKey().addKey(prevItem);
                const prevDateValidToBc = prevItemBc.navigate(TemporalEntityEntity.DateValidTo);

                storage.addActiveField(prevDateValidToBc, { revalidate: true });
            }
        }
        // to prevent actual error blinking, will be revalidated on blur.
        storage.clearError(args.bindingContext);

        // when DateValidFrom/To is changed, we need to re-render whole SmartFastEntryList
        // to re-render SimpleTimeline (could be changed for two line items for SimpleTimeLineStatus.Future)
        this.props.storage.addActiveField(this.getBindingContext());
    };

    handleLineItemsChange = (args: ISmartFieldChange): void => {
        this.props.storage.handleLineItemsChange(args);

        this.props.onChange?.(args);
        this.handleDateValidChange(args);
        this.props.storage.refreshFields(args.triggerAdditionalTasks);

        if (args.triggerAdditionalTasks) {
            // to ensure that the add button isDisabled callback is called (can be affected by e.g. date changes)
            this.props.storage.refresh();
        }
    };

    handleLineItemsBlur = async (args: ISmartFieldBlur): Promise<void> => {
        await this.props.storage.handleBlur(args);

        if ([TemporalEntityEntity.DateValidFrom, TemporalEntityEntity.DateValidTo].includes(args.bindingContext.getPath() as TemporalEntityEntity)) {
            // validate date granularity explicitly as it's not part of the validation scheme
            this.validateDateGranularity(args.bindingContext);
        }

        this.props.onBlur?.(args);
        this.props.storage.refresh();
    };

    handleLineItemsAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        const storage = this.props.storage;
        if (args.actionType === ActionType.Add && this._isSynced) {
            // while synchronized, we use Add button to cancel synchronization -> don't add new item
            this._isSynced = false;
            this.entity[this.temporalPropertyBagPath] = markHistoryValuesNotSynced(this.entity[this.temporalPropertyBagPath]);
            this.origEntity[this.temporalPropertyBagPath] = this.entity[this.temporalPropertyBagPath];
            storage.refresh();
            return;
        }

        storage.handleLineItemsAction(args);

        if (args.actionType === ActionType.Add) {
            const [item] = args.affectedItems;
            this.setItemDefaultValues(item as TTemporal);
            this.validateAll();
        } else if (args.actionType === ActionType.Remove) {
            // last item of cannotBeEnded should always have infinity in DateValidTo
            const items = this.getItems();
            const lastItem = items[items.length - 1];

            if (lastItem.DateValidTo !== DATE_MAX) {
                storage.setValue(this.getBindingContext().addKey(lastItem).navigate("DateValidTo"), DATE_MAX);
            }

            this.validateAll();
        }

        storage.refresh();
    };

    /** Validates all fields inside the dialog,
     * returns true if ok, false if error */
    validateAll = async (): Promise<boolean> => {
        const items = this.getItems();
        const columns = this.getColumns();
        const promises: Promise<IValidationError>[] = [];

        for (const item of items) {
            const itemBc = this.getBindingContext().addKey(item);

            for (const column of columns) {
                promises.push(
                    this.props.storage.validateField(itemBc.navigate(column.id))
                );
            }
        }

        const res = await Promise.all(promises);

        this.props.storage.refreshFields();


        return res.every(error => !error);
    };

    handleConfirm = async (): Promise<void> => {
        const { storage, temporalItemBindingContext } = this.props;

        if (!(await this.validateAll()) || !this.validateDates()) {
            storage.refreshFields();
            return;
        }

        try {
            const properties = this.getGroupingProperties();
            const ungrouped = ungroupAndOptimizeRanges(this.entity[this.temporalPropertyBagPath], this.oldTemporalBag, properties);
            const current = getCurrentBagFromUngrouped(ungrouped);
            // current might be found, but the range might be synchronized (with null values).
            // we need to find the correct value from default to put it to the current property bag

            // only apply CurrentTemporalPropertyBag if it exists in the entity
            // not the case for Company/VatStatuses
            if (temporalItemBindingContext.isValidNavigation(TemporalEntityProp.CurrentTemporalPropertyBag)) {
                const propertyBagBc = temporalItemBindingContext.navigate(TemporalEntityProp.CurrentTemporalPropertyBag);
                if (current) {
                    if (!this.entity.CurrentTemporalPropertyBag) {
                        this.entity.CurrentTemporalPropertyBag = {};
                    }
                    for (const prop of properties) {
                        this.entity.CurrentTemporalPropertyBag[prop] = current[prop];
                        const sourceCodePropName = `${prop}${EntityValueSourcePropNameEnumSuffix}`;
                        if (propertyBagBc.isValidNavigation(sourceCodePropName)) {
                            this.entity.CurrentTemporalPropertyBag[sourceCodePropName] = current[SourceCodeLocalPath] ?? PrEntityValueSourceCode.Entity;
                        }
                    }
                    this.entity.CurrentTemporalPropertyBag.DateValidFrom = current.DateValidFrom;
                    this.entity.CurrentTemporalPropertyBag.DateValidTo = current.DateValidTo;
                } else {
                    if (this.entity.CurrentTemporalPropertyBag) {
                        // only remove grouping properties,
                        // we don't want to affect other, unrelated properties that can have current value
                        for (const prop of properties) {
                            delete this.entity.CurrentTemporalPropertyBag[prop];
                        }
                    }
                }
            }

            // finally set correct new IDs to the items, so they can be processed by data updates, etc...
            ungrouped.forEach((item, idx) => {
                if (!item.Id) {
                    item[BindingContext.NEW_ENTITY_ID_PROP] = idx;
                }
            });

            // finally clear source code from history values and group null ranges created with extractHistory to one empty range
            this.entity[this.temporalPropertyBagPath] = ungrouped;
            this.origEntity[this.temporalPropertyBagPath] = this.oldOrigTemporalBag;
            this.close(current);
        } catch (e) {
            logger.error("error ranges ungrouping", e);
        }
    };

    handleCancel = (): void => {
        this.entity[this.temporalPropertyBagPath] = this.oldTemporalBag;
        this.origEntity[this.temporalPropertyBagPath] = this.oldOrigTemporalBag;
        this.close();
    };

    getDialogSettings = memoizeOne((): TTemporalDialogSettings => {
        const fieldInfo = this.props.storage.getInfo(this.props.bindingContext);

        return fieldInfo.fieldSettings.temporalDialog ?? {};
    }, () => []);

    getCustomPreContent = (args: ICustomPreContentArgs): React.ReactNode => {
        if (args.items?.length < 2) {
            return null;
        }

        const status = getTimelineStatus(this.props.storage, args.item as TTemporal);
        const nextItem = args.items[args.index + 1] as TTemporal;
        let isNextFuture = false;

        if (nextItem) {
            isNextFuture = getTimelineStatus(this.props.storage, nextItem) === SimpleTimeLineStatus.Future;
        }

        // some margins differ for when the fast entry items are wrapped and not wrapped (LabelStatus.Removed)
        const topOffset = args.labelStatus === LabelStatus.Removed ? 18 : 35;
        const bottomLineOverflow = args.labelStatus === LabelStatus.Removed ? FIELD_VER_MARGIN_SMALL : FIELD_VER_MARGIN;

        return (
            <div style={{ marginRight: `${SIMPLE_TIMELINE_POINT_MARGIN}px`, position: "relative" }}>
                <SimpleTimelineItem isFirst={args.index === 0}
                                    isLast={args.index === args.items.length - 1}
                                    status={status}
                                    isNextFuture={isNextFuture}
                                    topOffset={`${topOffset}px`}
                                    bottomLineOverflow={bottomLineOverflow}
                />
            </div>
        );
    };

    getAddDisabledAlert = (): IAlertProps => {
        return {
            isSmall: true,
            status: Status.Warning,
            title: this.props.storage.t("Components:TemporalProperty.WarningOnlyOnePlanPossible")
        };
    };

    getItems = (): TTemporal[] => {
        return this.entity?.[this.temporalPropertyBagPath] ?? [];
    };

    render() {
        const storage = this.props.storage as FormStorage;
        const dialogSettings = this.getDialogSettings();
        const title = dialogSettings.dialogTitle;
        const items = this.getItems();
        // we have decided not to disable the add button, if it would change in the future, consider all
        // the interactions... Does it make sense to let user only one future plan? What is decisive date for it?
        const addDisabled = false;

        const isSynced = this._isSynced;
        const isReadOnly = isFormReadOnly({ storage });

        return (
            <DialogStyledTemporalPropertyDialog
                showTimeLine={items.length > 1}
                onConfirm={this.handleConfirm}
                onClose={this.handleCancel}
                busy={this.props.busy}
                title={title}
                footer={<ConfirmationButtons
                    confirmText={storage.t("Common:General.Confirm")}
                    isDisabled={isSynced || isReadOnly}
                    onConfirm={this.handleConfirm}
                    onCancel={this.handleCancel}
                    useWrapper={false}
                />}>
                <SmartFastEntryList
                    bindingContext={this.getBindingContext()}
                    columns={this.getColumns()}
                    storage={storage}
                    onChange={this.handleLineItemsChange}
                    onAction={this.handleLineItemsAction}
                    useLabelWrapping={true}
                    labelsLeftMargin={items?.length > 1 ? SIMPLE_TIMELINE_POINT_SIZE + SIMPLE_TIMELINE_POINT_MARGIN : 0}
                    canAdd={!isReadOnly}
                    isAddDisabled={addDisabled}
                    customAddButtonText={storage.t(`Components:TemporalProperty.${isSynced ? "CancelSync" : "PlanChange"}`)}
                    addDisabledAlert={addDisabled ? this.getAddDisabledAlert() : null}
                    isCollapsible={false}
                    isReadOnly={isReadOnly}
                    renderAsFastEntryForReadOnly
                    onBlur={this.handleLineItemsBlur}
                    canReorder={false}
                    isItemRemovable={isSynced ? false : this.isItemRemovable}
                    isItemCloneable={false}
                    isItemSelectable={false}
                    groupId={"temporalPropertyDialog"}
                    customPreContent={this.getCustomPreContent}
                    order={null}
                />
            </DialogStyledTemporalPropertyDialog>
        );
    }
}

export default withBusyIndicator({ defaultBusyState: true })(SmartTemporalPropertyDialog);



